import { useApiTestingLanguage } from './api-testing/useApiTestingLanguage';
import { showJiraTicketSuccess } from './api-testing/apiTestingFeedback';
import APITestingErrorBoundary from './api-testing/ApiTestingErrorBoundary';
import useOverviewHistory, { clearOverviewHistory } from './dashboard/useOverviewHistory';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../utils/backendURL';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import IDTooltip from './IDTooltip';
// Refactored components
import { 
  ChainCard, 
  ChainExecutionResult, 
  ChainBuilderContent,
  TestCard,
  TestEmptyState,
  EnvironmentCard,
  CredentialCard,
  ExecutionHistoryCard,
  TestFormModal,
  ScheduleModal,
  ExecutionDetailsModal,
  ImportCollectionModal,
  ImportResultsModal
} from './api-testing';
import { 
  Play,
  Plus,
  Settings,
  Key,
  Globe,
  Code,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Edit,
  Trash2,
  Brain,
  Zap,
  Copy,
  Eye,
  EyeOff,
  Server,
  Upload,
  FileText,
  Download,
  RefreshCw,
  Target,
  Calendar,
  Search,
  X as XIcon,
  Folder,
  Bug,
  ExternalLink,
  Link2,
  ArrowRight,
  GripVertical,
  PlayCircle,
  StopCircle,
  SkipForward,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

// Backend URL imported from utility at top

// Debug mode - set to false to disable console.log messages
const DEBUG_MODE = false;
const debugLog = (...args) => {
  if (DEBUG_MODE) debugLog(...args);
};

// Add axios response interceptor to handle HTML responses
axios.interceptors.response.use(
  (response) => {
    // Check if response is supposed to be JSON but got HTML
    const contentType = response.headers['content-type'];
    if (contentType && contentType.includes('text/html') && response.config.headers['Accept']?.includes('application/json')) {
      console.warn('Received HTML response when expecting JSON:', response.data);
      // Create a proper error response
      throw new Error('Server returned HTML instead of JSON. Please check your API endpoint.');
    }
    return response;
  },
  (error) => {
    // Handle cases where the server returns HTML error pages
    if (error.response && error.response.data && typeof error.response.data === 'string' && 
        (error.response.data.includes('<!DOCTYPE') || error.response.data.includes('<html'))) {
      console.warn('Received HTML error page:', error.response.data);
      error.message = 'Server error - please check your connection and try again';
    }
    return Promise.reject(error);
  }
);

const APITesting = ({ user, selectedTestFromDashboard, onTestSelected }) => {
  const { t, language, showSuccess, showError, showWarning, showInfo } = useApiTestingLanguage();
  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // Get sub-tab from URL or default to 'tests'
  const getInitialSubTab = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'tests';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialSubTab);
  
  // Update URL when sub-tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(window.location.search);
    if (tab !== 'history') clearOverviewHistory(params);
    params.set('tab', tab);
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({ tab }, '', newUrl);
    
    // Auto-load schedules when switching to schedules tab
    if (tab === 'schedules' && schedules.length === 0 && !loadingSchedules) {
      loadSchedules();
    }
  };
  const [apiTests, setApiTests] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [selectedTest, setSelectedTest] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Pagination state
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  
  // Project state
  const [projects, setProjects] = useState([]);
  const [selectedProjectFilter, setSelectedProjectFilter] = useState(null); // null = all projects
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Dialogs state
  const [showCreateTest, setShowCreateTest] = useState(false);
  const [showCreateEnvironment, setShowCreateEnvironment] = useState(false);
  const [showCreateCredential, setShowCreateCredential] = useState(false);
  const [showExecutionDetails, setShowExecutionDetails] = useState(false);
  
  // Execution history state
  const [executions, setExecutions] = useState([]);
  const [selectedTestForHistory, setSelectedTestForHistory] = useState(null);
  const [loadingExecutions, setLoadingExecutions] = useState(false);
  
  // Schedule state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedTestForSchedule, setSelectedTestForSchedule] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    schedule_type: 'one_time',
    scheduled_datetime: '',
    recurrence_pattern: 'daily',
    start_date: '',
    end_date: '',
    time_of_day: '09:00',
    timezone: 'America/New_York',
    email_notifications: true,
    in_app_notifications: true
  });

  // Jira integration states
  const [jiraIntegrations, setJiraIntegrations] = useState([]);
  const [creatingJiraTicket, setCreatingJiraTicket] = useState(false);

  // Confirmation modal states
  const [showDeleteScheduleModal, setShowDeleteScheduleModal] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [showDeleteEnvironmentModal, setShowDeleteEnvironmentModal] = useState(false);
  const [environmentToDelete, setEnvironmentToDelete] = useState(null);
  const [showDeleteCredentialModal, setShowDeleteCredentialModal] = useState(false);
  const [credentialToDelete, setCredentialToDelete] = useState(null);
  const [showDeleteTestModal, setShowDeleteTestModal] = useState(false);
  const [testToDelete, setTestToDelete] = useState(null);

  // Forms state
  const [testForm, setTestForm] = useState({
    name: '',
    description: '',
    method: 'GET',
    url: '',
    headers: [], // Array of {enabled: true, key: '', value: '', description: ''}
    body: '',
    body_type: 'json',
    auth_type: '',
    credential_id: '',
    project_id: '', // Project/folder assignment
    environment_id: '',
    assertions: [],
    extractions: [] // Array of {variable_name: '', source: 'body', path: '', fallback: '', description: ''}
  });
  
  // Common HTTP headers for autocomplete suggestions
  const commonHeaders = [
    'Accept', 'Accept-Charset', 'Accept-Encoding', 'Accept-Language',
    'Authorization', 'Cache-Control', 'Content-Type', 'Content-Length',
    'Cookie', 'Host', 'Origin', 'Referer', 'User-Agent',
    'X-API-Key', 'X-Auth-Token', 'X-Requested-With', 'X-Custom-Header'
  ];
  
  const commonContentTypes = [
    'application/json',
    'application/x-www-form-urlencoded',
    'multipart/form-data',
    'text/plain',
    'text/html',
    'application/xml',
    'text/xml'
  ];

  const [environmentForm, setEnvironmentForm] = useState({
    name: '',
    description: '',
    variables: {}
  });
  const [environmentVariablesText, setEnvironmentVariablesText] = useState('');

  const [credentialForm, setCredentialForm] = useState({
    name: '',
    type: 'bearer_token',
    value: '',
    environment_id: ''
  });

  // Chain (workflow) state
  const [chains, setChains] = useState([]);
  const [showCreateChain, setShowCreateChain] = useState(false);
  const [editingChain, setEditingChain] = useState(null);
  const [chainForm, setChainForm] = useState({
    name: '',
    description: '',
    steps: [],
    environment_id: '',
    project_id: ''
  });
  const [chainExecuting, setChainExecuting] = useState(null);
  const [chainExecutionResult, setChainExecutionResult] = useState(null);
  const [showChainResult, setShowChainResult] = useState(false);
  
  // PRG Pattern: Track in-flight requests to prevent duplicate submissions
  const [chainSubmitting, setChainSubmitting] = useState(false);
  const [lastSubmissionId, setLastSubmissionId] = useState(null);

  // Edit states
  const [editingEnvironment, setEditingEnvironment] = useState(null);
  const [editingCredential, setEditingCredential] = useState(null);
  const [editingTest, setEditingTest] = useState(null);
  const [showEditEnvironment, setShowEditEnvironment] = useState(false);
  const [showEditCredential, setShowEditCredential] = useState(false);

  const [aiPrompt, setAiPrompt] = useState('');
  const [showAiGenerator, setShowAiGenerator] = useState(false);

  // Import-related state
  const [imports, setImports] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showImportResults, setShowImportResults] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    loadApiTests();
    loadEnvironments();
    loadCredentials();
    loadImports();
    loadChains();
  }, []);

  // Load Jira integrations only when user is available and authenticated
  useEffect(() => {
    if (user && user.email) {
      loadJiraIntegrations();
    }
  }, [user]);

  // Load projects on component mount (Phase 2: Non-breaking)
  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoadingProjects(true);
        const response = await axios.get(
          `${BACKEND_URL}/api/projects`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
        );
        setProjects(response.data || []);
        debugLog('✅ Projects loaded:', response.data?.length || 0);
      } catch (error) {
        console.error('❌ Failed to load projects:', error);
        setProjects([]); // Graceful fallback - continue without projects
      } finally {
        setLoadingProjects(false);
      }
    };

    loadProjects();
  }, []); // Run once on mount

  useOverviewHistory('api_test', selectedTestFromDashboard, onTestSelected, (test) => viewTestHistory(test));
  
  // Trigger search when searchQuery changes
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadApiTests(); // Will use current searchQuery and selectedProjectFilter
    }, 300); // 300ms debounce

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const loadApiTests = async (skipParam = null, limitParam = null, searchParam = null, projectIdParam = null) => {
    // Determine if this is a "load more" operation - moved outside try to be accessible in catch
    const loadMore = skipParam !== null && skipParam > 0;
    
    try {
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setOffset(0);
      }
      
      const currentOffset = skipParam !== null ? skipParam : (loadMore ? offset : 0);
      const currentLimit = limitParam || 10;
      const currentSearch = searchParam !== null ? searchParam : searchQuery;
      // Use projectIdParam if explicitly passed (including null), otherwise use state
      const currentProjectId = projectIdParam !== undefined ? projectIdParam : selectedProjectFilter;
      
      // Build query string
      let queryString = `limit=${currentLimit}&offset=${currentOffset}`;
      if (currentSearch) {
        queryString += `&search=${encodeURIComponent(currentSearch)}`;
      }
      if (currentProjectId) {
        queryString += `&project_id=${encodeURIComponent(currentProjectId)}`;
      }
      
      const response = await axios.get(
        `${BACKEND_URL}/api/api-testing/tests?${queryString}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      
      if (loadMore) {
        setApiTests(prev => [...prev, ...response.data.tests]);
      } else {
        setApiTests(response.data.tests);
      }
      
      setHasMore(response.data.has_more);
      setOffset(currentOffset + currentLimit);
    } catch (error) {
      console.error('Failed to load API tests:', error);
      if (!loadMore) {
        setApiTests([]); // Set empty array to prevent crashes
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };
  
  // Handle search with debounce effect
  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const loadEnvironments = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/api-testing/environments`);
      setEnvironments(response.data);
    } catch (error) {
      console.error('Failed to load environments:', error);
      setEnvironments([]); // Set empty array to prevent crashes
    }
  };

  const loadCredentials = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/api-testing/credentials`);
      setCredentials(response.data);
    } catch (error) {
      console.error('Failed to load credentials:', error);
      setCredentials([]); // Set empty array to prevent crashes
    }
  };

  const loadImports = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/api-testing/imports`);
      setImports(response.data);
    } catch (error) {
      console.error('Failed to load imports:', error);
    }
  };

  // Load Jira integrations for the user
  const loadJiraIntegrations = async () => {
    // Early return if no authentication
    const token = localStorage.getItem('token');
    if (!token) {
      setJiraIntegrations([]);
      return;
    }
    
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/integrations`,
        { headers: getAuthHeaders() }
      );
      // Filter only active Jira integrations
      const jiraOnly = response.data.filter(
        integration => integration.integration_type === 'jira' && integration.is_active
      );
      setJiraIntegrations(jiraOnly);
    } catch (error) {
      // Don't log errors for missing integrations - this is normal for new users
      if (error.response?.status !== 404 && error.response?.status !== 401) {
        console.error('Failed to load Jira integrations:', error);
      }
      setJiraIntegrations([]);
    }
  };

  // Chain (workflow) functions
  const loadChains = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/api-testing/chains`,
        { headers: getAuthHeaders() }
      );
      
      // Fetch enriched details for each chain
      const enrichedChains = await Promise.all(
        response.data.map(async (chain) => {
          try {
            const detailResponse = await axios.get(
              `${BACKEND_URL}/api/api-testing/chains/${chain.id}`,
              { headers: getAuthHeaders() }
            );
            return detailResponse.data;
          } catch (err) {
            return chain; // Return basic chain if enrichment fails
          }
        })
      );
      
      setChains(enrichedChains);
    } catch (error) {
      console.error('Failed to load chains:', error);
    }
  };

  const createOrUpdateChain = async () => {
    if (!chainForm.name || chainForm.steps.length === 0) {
      showError(t("Please provide a chain name and add at least one step"));
      return;
    }

    // PRG Pattern: Prevent duplicate submissions
    if (chainSubmitting) {
      showWarning(t("Request already in progress, please wait..."));
      return;
    }

    // Generate unique submission ID for this request
    const submissionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    setChainSubmitting(true);
    setLastSubmissionId(submissionId);
    setLoading(true);
    
    try {
      const payload = {
        name: chainForm.name,
        description: chainForm.description,
        steps: chainForm.steps.map((step, index) => ({
          test_id: step.test_id,
          order: index + 1,
          stop_on_fail: step.stop_on_fail ?? true,
          delay_ms: step.delay_ms || 0,
          condition: step.condition || null, // Conditional execution logic
          description: step.description || ''
        })),
        environment_id: chainForm.environment_id || null,
        project_id: chainForm.project_id || null,
        // PRG Pattern: Include idempotency key to prevent server-side duplicates
        idempotency_key: submissionId
      };

      if (editingChain) {
        await axios.put(
          `${BACKEND_URL}/api/api-testing/chains/${editingChain.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        showSuccess(t("Chain updated successfully"));
      } else {
        await axios.post(
          `${BACKEND_URL}/api/api-testing/chains`,
          payload,
          { headers: getAuthHeaders() }
        );
        showSuccess(t("Chain created successfully"));
      }

      // PRG Pattern: After successful POST, redirect to clean state (GET)
      await loadChains();
      resetChainForm();
      setShowCreateChain(false);
    } catch (error) {
      console.error('Failed to save chain:', error);
      showError(t("Failed to save chain"));
    } finally {
      setLoading(false);
      setChainSubmitting(false);
    }
  };

  const resetChainForm = () => {
    setChainForm({
      name: '',
      description: '',
      steps: [],
      environment_id: '',
      project_id: ''
    });
    setEditingChain(null);
  };

  const editChain = (chain) => {
    setChainForm({
      name: chain.name,
      description: chain.description || '',
      steps: chain.steps.map(step => ({
        test_id: step.test_id,
        stop_on_fail: step.stop_on_fail ?? true,
        delay_ms: step.delay_ms || 0,
        condition: step.condition || '', // Load condition from saved chain
        description: step.description || '',
        test_details: step.test_details || {}
      })),
      environment_id: chain.environment_id || '',
      project_id: chain.project_id || ''
    });
    setEditingChain(chain);
    setShowCreateChain(true);
  };

  const deleteChain = async (chainId) => {
    try {
      await axios.delete(
        `${BACKEND_URL}/api/api-testing/chains/${chainId}`,
        { headers: getAuthHeaders() }
      );
      showSuccess(t("Chain deleted successfully"));
      await loadChains();
    } catch (error) {
      console.error('Failed to delete chain:', error);
      showError(t("Failed to delete chain"));
    }
  };

  const executeChain = async (chainId) => {
    // PRG Pattern: Prevent duplicate executions - check if already executing this chain
    if (chainExecuting === chainId) {
      showWarning(t("This chain is already running, please wait..."));
      return;
    }
    
    // Also prevent executing any chain if another is running
    if (chainExecuting !== null) {
      showWarning(t("Another chain is currently running, please wait..."));
      return;
    }
    
    setChainExecuting(chainId);
    setChainExecutionResult(null);
    
    // Generate unique execution ID for idempotency
    const executionRequestId = `exec-${chainId}-${Date.now()}`;
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/api-testing/chains/${chainId}/execute`,
        { idempotency_key: executionRequestId }, // PRG: Include idempotency key
        { headers: getAuthHeaders() }
      );
      
      setChainExecutionResult(response.data);
      setShowChainResult(true);
      
      if (response.data.status === 'PASS') {
        showSuccess(t("Chain executed successfully! All {v0} steps passed.", {v0: response.data.total_steps}));
      } else if (response.data.status === 'PARTIAL') {
        showWarning(t("Chain partially completed. {v0}/{v1} steps passed.", {v0: response.data.passed_steps, v1: response.data.total_steps}));
      } else {
        showError(t("Chain execution failed. {v0} step(s) failed.", {v0: response.data.failed_steps}));
      }
    } catch (error) {
      console.error('Failed to execute chain:', error);
      showError(t("Failed to execute chain"));
    } finally {
      setChainExecuting(null);
    }
  };

  const addStepToChain = (testId) => {
    const test = apiTests.find(t => t.id === testId);
    if (!test) return;
    
    setChainForm({
      ...chainForm,
      steps: [...chainForm.steps, {
        test_id: testId,
        stop_on_fail: true,
        delay_ms: 0,
        condition: '', // Conditional execution (e.g., "status_code == 200")
        description: '',
        test_details: {
          name: test.name,
          method: test.method,
          url: test.url,
          extractions: test.extractions || []
        }
      }]
    });
  };

  const removeStepFromChain = (index) => {
    setChainForm({
      ...chainForm,
      steps: chainForm.steps.filter((_, i) => i !== index)
    });
  };

  const moveStepInChain = (fromIndex, direction) => {
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= chainForm.steps.length) return;
    
    const newSteps = [...chainForm.steps];
    [newSteps[fromIndex], newSteps[toIndex]] = [newSteps[toIndex], newSteps[fromIndex]];
    setChainForm({ ...chainForm, steps: newSteps });
  };

  // Create Jira ticket from API test execution failure
  const createJiraTicketFromExecution = async (execution) => {
    if (!execution) return;
    
    // Check if user has Jira integrations
    if (jiraIntegrations.length === 0) {
      showInfo(t("To create Jira tickets, please configure a Jira integration in the Integrations tab first."));
      return;
    }
    
    // Use the first active Jira integration
    const jiraIntegration = jiraIntegrations[0];
    
    setCreatingJiraTicket(true);
    
    try {
      // Prepare ticket data for API test
      const ticketData = {
        test_name: execution.test_name || selectedTest?.name || 'Unknown API Test',
        test_type: 'API Test',
        error_message: execution.error_message || 'API test execution failed',
        repro_steps: generateAPITestReproSteps(execution)
      };
      
      // Make API call to create Jira ticket
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/integrations/${jiraIntegration.id}/create-jira-ticket`,
        ticketData,
        { 
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          params: {
            test_name: ticketData.test_name,
            test_type: ticketData.test_type
          }
        }
      );
      
      if (response.data.success) {
        showJiraTicketSuccess(response.data.issue_key, response.data.issue_url, language);
      }
    } catch (error) {
      console.error('Failed to create Jira ticket:', error);
      showError(t("Failed to create Jira ticket: {v0}", {v0: error.response?.data?.detail || error.message}));
    } finally {
      setCreatingJiraTicket(false);
    }
  };

  // Generate reproduction steps for API test failure
  const generateAPITestReproSteps = (execution) => {
    const steps = ['**Automated API Test Execution Failed**', ''];
    
    steps.push(`**Test Name:** ${execution.test_name || 'Unknown'}`);
    if (execution.id) {
      steps.push(`**Execution ID:** ${execution.id}`);
    }
    if (execution.execution_number) {
      steps.push(`**Execution Number:** #${execution.execution_number}`);
    }
    steps.push('');
    
    steps.push('**Request Details:**');
    steps.push(`- Method: ${execution.method || 'N/A'}`);
    steps.push(`- URL: ${execution.url || 'N/A'}`);
    steps.push('');
    
    if (execution.started_at) {
      steps.push(`**Started At:** ${new Date(execution.started_at).toLocaleString()}`);
    }
    if (execution.response_time) {
      steps.push(`**Response Time:** ${execution.response_time.toFixed(0)}ms`);
    }
    steps.push('');
    
    if (execution.response_status_code) {
      steps.push(`**Response Status:** ${execution.response_status_code}`);
    }
    
    if (execution.error_message) {
      steps.push('');
      steps.push('**Error Message:**');
      steps.push('```');
      steps.push(execution.error_message);
      steps.push('```');
    }
    
    if (execution.assertion_results && execution.assertion_results.length > 0) {
      steps.push('');
      steps.push('**Assertion Results:**');
      execution.assertion_results.forEach((result, index) => {
        const status = result.passed ? '✅' : '❌';
        const assertionDesc = result.assertion.description || 
          `${result.assertion.type} ${result.assertion.operator} ${result.assertion.expected_value}`;
        steps.push(`${index + 1}. ${status} ${assertionDesc}`);
        if (!result.passed && result.actual_value !== undefined) {
          steps.push(`   - Expected: ${result.assertion.expected_value}`);
          steps.push(`   - Actual: ${typeof result.actual_value === 'object' ? JSON.stringify(result.actual_value) : result.actual_value}`);
        }
      });
    }
    
    if (execution.response_body && execution.response_body.length < 500) {
      steps.push('');
      steps.push('**Response Body:**');
      steps.push('```');
      steps.push(execution.response_body);
      steps.push('```');
    }
    
    return steps.join('\n');
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const uploadFile = async () => {
    if (!selectedFile) {
      showWarning(t("Please select a file to upload"));
      return;
    }

    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${BACKEND_URL}/api/api-testing/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setImportResult(response.data);
      setShowImportResults(true);
      setShowImportDialog(false);
      setSelectedFile(null);
      
      // Reload data
      await loadApiTests();
      await loadEnvironments();
      await loadImports();
      
    } catch (error) {
      console.error('Failed to upload file:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to import file';
      showError(errorMessage);
    } finally {
      setImportLoading(false);
    }
  };

  const getImportStatusColor = (status) => {
    const colors = {
      'completed': 'bg-green-100 text-green-800',
      'failed': 'bg-red-100 text-red-800',
      'processing': 'bg-yellow-100 text-yellow-800',
      'pending': 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getImportStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'processing': return <Clock className="h-4 w-4 text-yellow-500 animate-spin" />;
      case 'pending': return <Clock className="h-4 w-4 text-blue-500" />;
      default: return <AlertTriangle className="h-4 w-4 text-gray-500" />;
    }
  };

  const generateApiTestFromAI = async () => {
    if (!aiPrompt.trim()) {
      showWarning(t("Please enter a test description"));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/api-testing/generate`, {
        description: aiPrompt,
        environment_id: environmentForm.environment_id || null
      });

      await loadApiTests();
      setAiPrompt('');
      setShowAiGenerator(false);
      showSuccess(t("AI test generated successfully!"));
    } catch (error) {
      console.error('Failed to generate API test:', error);
      if (error.response?.status === 403) {
        showError(t("Monthly test generation limit reached. Please upgrade your plan."));
      } else {
        showError(t("Failed to generate test. Please try again."));
      }
    } finally {
      setLoading(false);
    }
  };

  const createApiTest = async () => {
    setLoading(true);
    try {
      // Convert headers array to object format for backend
      // Only include enabled headers
      const headersObject = {};
      if (Array.isArray(testForm.headers)) {
        testForm.headers.forEach(header => {
          if (header.enabled && header.key && header.key.trim()) {
            headersObject[header.key.trim()] = header.value || '';
          }
        });
      }
      
      const payload = {
        ...testForm,
        headers: headersObject
      };
      
      if (editingTest) {
        // Update existing test
        await axios.put(`${BACKEND_URL}/api/api-testing/tests/${editingTest.id}`, payload);
      } else {
        // Create new test
        await axios.post(`${BACKEND_URL}/api/api-testing/tests`, payload);
      }
      
      await loadApiTests();
      setTestForm({
        name: '',
        description: '',
        method: 'GET',
        url: '',
        headers: [],
        body: '',
        body_type: 'json',
        auth_type: '',
        credential_id: '',
        environment_id: '',
        project_id: '',  // Reset project selection
        assertions: [],
        extractions: []
      });
      setEditingTest(null);
      setShowCreateTest(false);
    } catch (error) {
      console.error('Failed to save API test:', error);
      showError(t("Failed to save test. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const executeApiTest = async (testId) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/api-testing/tests/${testId}/execute`);
      
      // Show execution results immediately
      setSelectedTest(response.data);
      setShowExecutionDetails(true);
      
      // Refresh tests list and execution history
      await loadApiTests();
      
      // If we have a selected test for history, refresh its execution history
      if (selectedTestForHistory && selectedTestForHistory.id === testId) {
        await loadExecutions(testId);
      }
    } catch (error) {
      console.error('Failed to execute API test:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to execute test. Please check your configuration.';
      showError(errorMessage);
    }
  };

  const loadExecutions = async (testId) => {
    if (!testId) return;
    
    setLoadingExecutions(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/api-testing/tests/${testId}/executions`);
      setExecutions(response.data);
    } catch (error) {
      console.error('Failed to load executions:', error);
      setExecutions([]);
    } finally {
      setLoadingExecutions(false);
    }
  };

  const viewTestHistory = async (test) => {
    setSelectedTestForHistory(test);
    setActiveTab('history');
    await loadExecutions(test.id);
  };

  const openScheduleModal = (test) => {
    setSelectedTestForSchedule(test);
    setShowScheduleModal(true);
    // Reset form
    setScheduleForm({
      schedule_type: 'one_time',
      scheduled_datetime: '',
      recurrence_pattern: 'daily',
      start_date: '',
      end_date: '',
      time_of_day: '09:00',
      timezone: 'America/New_York',
      email_notifications: true,
      in_app_notifications: true
    });
  };

  const loadSchedules = async () => {
    setLoadingSchedules(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/schedules`);
      const schedulesData = response.data;
      
      // Filter to only show API test schedules
      const apiSchedules = schedulesData.filter(schedule => schedule.test_type === 'api_test');
      
      // Enhance schedules with execution details
      const enhancedSchedules = await Promise.all(
        apiSchedules.map(async (schedule) => {
          try {
            // Get the latest execution for this schedule
            const executionsResponse = await axios.get(`${BACKEND_URL}/api/schedules/${schedule.id}/executions`);
            const executions = executionsResponse.data;
            
            if (executions && executions.length > 0) {
              // Get the most recent execution
              const latestExecution = executions[0]; // Assuming they're sorted by most recent
              
              // Get execution number directly from schedule execution, or fallback to fetching from test executions
              let executionNumber = latestExecution.execution_number || null;
              
              if (!executionNumber && latestExecution.execution_id && schedule.test_type === 'api_test') {
                try {
                  const testExecutionsResponse = await axios.get(`${BACKEND_URL}/api/api-testing/tests/${schedule.test_id}/executions`);
                  const testExecutions = testExecutionsResponse.data;
                  const matchingExecution = testExecutions.find(exec => exec.id === latestExecution.execution_id);
                  if (matchingExecution) {
                    executionNumber = matchingExecution.execution_number;
                  }
                } catch (error) {
                  console.warn('Could not load execution number:', error);
                }
              }
              
              return {
                ...schedule,
                latestExecution: {
                  ...latestExecution,
                  executionNumber,
                  response_time: latestExecution.response_time
                }
              };
            }
            
            return schedule;
          } catch (error) {
            console.warn(`Could not load executions for schedule ${schedule.id}:`, error);
            return schedule;
          }
        })
      );
      
      setSchedules(enhancedSchedules);
    } catch (error) {
      console.error('Failed to load schedules:', error);
      setSchedules([]);
    } finally {
      setLoadingSchedules(false);
    }
  };

  const createSchedule = async () => {
    if (!selectedTestForSchedule) return;

    setLoading(true);
    try {
      const scheduleData = {
        test_id: selectedTestForSchedule.id,
        test_type: 'api_test',
        schedule_type: scheduleForm.schedule_type,
        timezone: scheduleForm.timezone,
        email_notifications: scheduleForm.email_notifications,
        in_app_notifications: scheduleForm.in_app_notifications
      };

      if (scheduleForm.schedule_type === 'one_time') {
        if (!scheduleForm.scheduled_datetime) {
          showWarning(t("Please select a date and time for the scheduled execution"));
          return;
        }
        // Convert local datetime to the selected timezone properly
        // The datetime-local input gives us local time, so we need to specify it's in the selected timezone
        const localDateTime = new Date(scheduleForm.scheduled_datetime);
        
        // Create a date string that represents the time in the selected timezone
        // Format: YYYY-MM-DDTHH:MM:SS
        const dateTimeString = scheduleForm.scheduled_datetime;
        
        // Send the raw datetime string and let backend handle timezone conversion
        scheduleData.scheduled_datetime = dateTimeString;
      } else {
        if (!scheduleForm.start_date || !scheduleForm.time_of_day) {
          showWarning(t("Please select a start date and time for recurring execution"));
          return;
        }
        scheduleData.recurrence_pattern = scheduleForm.recurrence_pattern;
        scheduleData.start_date = new Date(scheduleForm.start_date).toISOString();
        scheduleData.time_of_day = scheduleForm.time_of_day;
        
        if (scheduleForm.end_date) {
          scheduleData.end_date = new Date(scheduleForm.end_date).toISOString();
        }
      }

      await axios.post(`${BACKEND_URL}/api/schedules`, scheduleData);
      
      setShowScheduleModal(false);
      await loadSchedules();
      showSuccess(t("Schedule created successfully!"));
      
    } catch (error) {
      console.error('Failed to create schedule:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create schedule';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const toggleSchedule = async (scheduleId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/schedules/${scheduleId}/toggle`);
      await loadSchedules();
    } catch (error) {
      console.error('Failed to toggle schedule:', error);
      showError(t("Failed to toggle schedule status"));
    }
  };

  const openDeleteScheduleModal = (schedule) => {
    setScheduleToDelete(schedule);
    setShowDeleteScheduleModal(true);
  };

  const deleteSchedule = async () => {
    if (!scheduleToDelete) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/schedules/${scheduleToDelete.id}`);
      await loadSchedules();
      showSuccess(t("Schedule deleted successfully"));
      setShowDeleteScheduleModal(false);
      setScheduleToDelete(null);
    } catch (error) {
      console.error('Failed to delete schedule:', error);
      showError(t("Failed to delete schedule"));
    }
  };

  const createEnvironment = async () => {
    setLoading(true);
    try {
      // Validate JSON before sending
      let variables = {};
      try {
        // If empty, use empty object
        if (environmentVariablesText.trim() === '') {
          variables = {};
        } else {
          variables = JSON.parse(environmentVariablesText);
        }
      } catch (err) {
        showError(t("Invalid JSON format in Variables field. Please check your syntax."));
        setLoading(false);
        return;
      }
      
      await axios.post(`${BACKEND_URL}/api/api-testing/environments`, {
        ...environmentForm,
        variables
      });
      await loadEnvironments();
      setEnvironmentForm({ name: '', description: '', variables: {} });
      setEnvironmentVariablesText('');
      setShowCreateEnvironment(false);
    } catch (error) {
      console.error('Failed to create environment:', error);
      showError(t("Failed to create environment."));
    } finally {
      setLoading(false);
    }
  };

  const createCredential = async () => {
    // Validate based on credential type
    if (credentialForm.type === 'oauth2') {
      if (!credentialForm.oauth2_token_url?.trim() || !credentialForm.oauth2_client_id?.trim() || !credentialForm.oauth2_client_secret?.trim()) {
        showWarning(t("Please fill in all OAuth2 fields (Token URL, Client ID, Client Secret)"));
        return;
      }
      // Package OAuth2 fields as JSON value
      credentialForm.value = JSON.stringify({
        token_url: credentialForm.oauth2_token_url,
        client_id: credentialForm.oauth2_client_id,
        client_secret: credentialForm.oauth2_client_secret
      });
    } else if (!credentialForm.value.trim()) {
      showWarning(t("Please enter credential value"));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: credentialForm.name,
        type: credentialForm.type,
        value: credentialForm.value,
        environment_id: credentialForm.environment_id
      };
      
      if (editingCredential) {
        // Update existing credential
        await axios.put(`${BACKEND_URL}/api/api-testing/credentials/${editingCredential.id}`, payload);
        setEditingCredential(null);
        setShowEditCredential(false);
      } else {
        // Create new credential
        await axios.post(`${BACKEND_URL}/api/api-testing/credentials`, payload);
        setShowCreateCredential(false);
      }
      await loadCredentials();
      setCredentialForm({ name: '', type: 'bearer_token', value: '', environment_id: '', oauth2_token_url: '', oauth2_client_id: '', oauth2_client_secret: '' });
      showSuccess(t("Credential saved successfully"));
    } catch (error) {
      console.error('Failed to save credential:', error);
      showError(t("Failed to save credential."));
    } finally {
      setLoading(false);
    }
  };

  const openDeleteEnvironmentModal = (environment) => {
    setEnvironmentToDelete(environment);
    setShowDeleteEnvironmentModal(true);
  };

  const deleteEnvironment = async () => {
    if (!environmentToDelete) return;

    setLoading(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/api-testing/environments/${environmentToDelete.id}`);
      await loadEnvironments();
      showSuccess(t("Environment deleted successfully"));
      setShowDeleteEnvironmentModal(false);
      setEnvironmentToDelete(null);
    } catch (error) {
      console.error('Failed to delete environment:', error);
      showError(t("Failed to delete environment."));
    } finally {
      setLoading(false);
    }
  };

  const openDeleteCredentialModal = (credential) => {
    setCredentialToDelete(credential);
    setShowDeleteCredentialModal(true);
  };

  const deleteCredential = async () => {
    if (!credentialToDelete) return;

    setLoading(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/api-testing/credentials/${credentialToDelete.id}`);
      await loadCredentials();
      showSuccess(t("Credential deleted successfully"));
      setShowDeleteCredentialModal(false);
      setCredentialToDelete(null);
    } catch (error) {
      console.error('Failed to delete credential:', error);
      showError(t("Failed to delete credential."));
    } finally {
      setLoading(false);
    }
  };

  const editApiTest = (test) => {
    // Convert headers from object format {key: value} to array format [{enabled, key, value, description}]
    let headersArray = [];
    if (test.headers) {
      if (Array.isArray(test.headers)) {
        // Already in array format
        headersArray = test.headers;
      } else {
        // Convert from object format
        headersArray = Object.entries(test.headers).map(([key, value]) => ({
          enabled: true,
          key: key,
          value: value,
          description: ''
        }));
      }
    }
    
    setTestForm({
      name: test.name,
      description: test.description || '',
      method: test.method,
      url: test.url,
      headers: headersArray,
      body: test.body || '',
      body_type: test.body_type || 'json',
      auth_type: test.auth_type || '',
      credential_id: test.credential_id || '',
      environment_id: test.environment_id || '',
      assertions: test.assertions || [],
      extractions: test.extractions || [],  // Include extractions for editing
      project_id: test.project_id || null  // Include project_id for editing
    });
    setEditingTest(test);
    setShowCreateTest(true);
  };

  const openDeleteTestModal = (test) => {
    setTestToDelete(test);
    setShowDeleteTestModal(true);
  };

  const deleteApiTest = async () => {
    if (!testToDelete) return;

    setLoading(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/api-testing/tests/${testToDelete.id}`);
      await loadApiTests();
      showSuccess(t("API test deleted successfully"));
      setShowDeleteTestModal(false);
      setTestToDelete(null);
    } catch (error) {
      console.error('Failed to delete API test:', error);
      showError(t("Failed to delete API test."));
    } finally {
      setLoading(false);
    }
  };

  const editEnvironment = (environment) => {
    setEditingEnvironment(environment);
    setEnvironmentForm({
      name: environment.name,
      description: environment.description || '',
      variables: environment.variables || {}
    });
    setEnvironmentVariablesText(JSON.stringify(environment.variables || {}, null, 2));
    setShowEditEnvironment(true);
  };

  const editCredential = (credential) => {
    setEditingCredential(credential);
    setCredentialForm({
      name: credential.name,
      type: credential.type,
      value: '', // Don't prefill password for security
      environment_id: credential.environment_id || ''
    });
    setShowEditCredential(true);
  };

  const updateEnvironment = async () => {
    setLoading(true);
    try {
      // Validate JSON before sending
      let variables = {};
      try {
        // If empty, use empty object
        if (environmentVariablesText.trim() === '') {
          variables = {};
        } else {
          variables = JSON.parse(environmentVariablesText);
        }
      } catch (err) {
        showError(t("Invalid JSON format in Variables field. Please check your syntax."));
        setLoading(false);
        return;
      }
      
      await axios.put(`${BACKEND_URL}/api/api-testing/environments/${editingEnvironment.id}`, {
        ...environmentForm,
        variables
      });
      await loadEnvironments();
      setEnvironmentForm({ name: '', description: '', variables: {} });
      setEnvironmentVariablesText('');
      setEditingEnvironment(null);
      setShowEditEnvironment(false);
    } catch (error) {
      console.error('Failed to update environment:', error);
      showError(t("Failed to update environment."));
    } finally {
      setLoading(false);
    }
  };

  const getMethodColor = (method) => {
    const colors = {
      GET: 'bg-blue-100 text-blue-800',
      POST: 'bg-green-100 text-green-800',
      PUT: 'bg-yellow-100 text-yellow-800',
      DELETE: 'bg-red-100 text-red-800',
      PATCH: 'bg-purple-100 text-purple-800'
    };
    return colors[method] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PASS':
      case 'passed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'FAIL':
      case 'failed': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'running': return <Clock className="h-4 w-4 text-blue-500 animate-spin" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PASS':
      case 'passed': return 'bg-green-500 text-white';
      case 'FAIL':
      case 'failed': return 'bg-red-500 text-white';
      case 'running': return 'bg-blue-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <IDTooltip id="API-01" tooltip={t("Screen ID: {v0} - Use this ID when reporting issues or requesting features for this section", { v0: "API-01" })}>
            <h2 className="text-2xl font-bold text-gray-900">{t("API Testing")}</h2>
          </IDTooltip>
          <p className="text-gray-600">{t("Create and execute API tests with AI-powered generation")}</p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Dialog open={showAiGenerator} onOpenChange={setShowAiGenerator}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700">
                <Brain className="h-4 w-4 mr-2" />
                {t("AI Generate")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{t("Generate API Test with AI")}</DialogTitle>
                <DialogDescription>
                  {t("Describe your API endpoint and let AI generate the test for you.")}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    {t("Describe your API test")}
                  </label>
                  <Textarea
                    placeholder={t("E.g., 'Test GET /api/users returns 200 with user list containing id, name, email fields'")}
                    className="min-h-[100px]"
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    {t("Environment (Optional)")}
                  </label>
                  <select
                    value={environmentForm.environment_id}
                    onChange={(e) => setEnvironmentForm({...environmentForm, environment_id: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="">{t("Select environment")}</option>
                    {environments.map(env => (
                      <option key={env.id} value={env.id}>{env.name}</option>
                    ))}
                  </select>
                </div>

                <Button 
                  onClick={generateApiTestFromAI}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600"
                >
                  {loading ? (
                    <>
                      <Clock className="h-4 w-4 mr-2 animate-spin" />
                      {t("Generating...")}
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4 mr-2" />
                      {t("Generate Test")}
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <TestFormModal
            open={showCreateTest}
            onOpenChange={(open) => {
              setShowCreateTest(open);
              if (!open) {
                setEditingTest(null);
                setTestForm({
                  name: '',
                  description: '',
                  method: 'GET',
                  url: '',
                  headers: [],
                  body: '',
                  body_type: 'json',
                  auth_type: '',
                  credential_id: '',
                  environment_id: '',
                  project_id: '',
                  assertions: [],
                  extractions: []
                });
              }
            }}
            testForm={testForm}
            setTestForm={setTestForm}
            editingTest={editingTest}
            environments={environments}
            credentials={credentials}
            projects={projects}
            onSave={createApiTest}
            loading={loading}
          />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
          <TabsTrigger value="tests">
            <Server className="h-4 w-4 mr-2" />
            {t("Tests")}
          </TabsTrigger>
          <TabsTrigger value="chains" data-testid="tab-chains">
            <Link2 className="h-4 w-4 mr-2" />
            {t("Chains")}
            {chains.length > 0 && (
              <span className="ml-1 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">
                {chains.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="schedules">
            <Calendar className="h-4 w-4 mr-2" />
            {t("Schedules")}
          </TabsTrigger>
          <TabsTrigger value="imports">
            <Upload className="h-4 w-4 mr-2" />
            {t("Imports")}
          </TabsTrigger>
          <TabsTrigger value="environments">
            <Globe className="h-4 w-4 mr-2" />
            {t("Environments")}
          </TabsTrigger>
          <TabsTrigger value="credentials">
            <Key className="h-4 w-4 mr-2" />
            {t("Credentials")}
          </TabsTrigger>
          <TabsTrigger value="history">
            <Clock className="h-4 w-4 mr-2" />
            {t("History")}
          </TabsTrigger>
        </TabsList>

        {/* API Tests Tab */}
        <TabsContent value="tests" className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
            <IDTooltip id="API-05" tooltip={t("Screen ID: {v0} - Use this ID when reporting issues or requesting features for this section", { v0: "API-05" })}>
              <h3 className="text-lg font-semibold text-gray-900">{t("API Tests")}</h3>
            </IDTooltip>
            
            {/* Search Bar */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={t("Search tests by name...")}
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => handleSearch("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          
          {/* Project Filter - Phase 2.2: Only shows if projects exist */}
          {projects.length > 0 && (
            <div className="mb-4 flex items-center space-x-3 bg-gray-50 p-3 rounded-lg border border-gray-200">
              <Folder className="h-5 w-5 text-indigo-600 flex-shrink-0" />
              <div className="flex items-center space-x-2 flex-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("Project:")}
                </label>
                <select
                  value={selectedProjectFilter || ''}
                  onChange={(e) => {
                    const projectId = e.target.value || null;
                    setSelectedProjectFilter(projectId);
                    setOffset(0); // Reset pagination
                    loadApiTests(0, 10, searchQuery, projectId); // Reload with filter
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                >
                  <option value="">{t("All Projects")}</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.icon} {project.name}
                    </option>
                  ))}
                </select>
                {selectedProjectFilter && (
                  <button
                    onClick={() => {
                      setSelectedProjectFilter(null);
                      setOffset(0);
                      loadApiTests(0, 10, searchQuery, null);
                    }}
                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    {t("Clear")}
                  </button>
                )}
              </div>
            </div>
          )}
          
          {apiTests.length === 0 ? (
            <TestEmptyState
              onAiGenerate={() => setShowAiGenerator(true)}
              onManualCreate={() => {
                // Reset form and editing state for fresh create
                setEditingTest(null);
                setTestForm({
                  name: '',
                  description: '',
                  method: 'GET',
                  url: '',
                  headers: [],
                  body: '',
                  body_type: 'json',
                  auth_type: '',
                  credential_id: '',
                  environment_id: '',
                  project_id: '',
                  assertions: [],
                  extractions: []
                });
                setShowCreateTest(true);
              }}
            />
          ) : (
            <div className="grid gap-4">
              {apiTests.map((test) => (
                <TestCard
                  key={test.id}
                  test={test}
                  projects={projects}
                  onRun={executeApiTest}
                  onViewHistory={viewTestHistory}
                  onSchedule={openScheduleModal}
                  onEdit={editApiTest}
                  onDelete={openDeleteTestModal}
                />
              ))}
              
              {/* Load More Button / Loading Indicator */}
              {loadingMore && (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              )}
              
              {hasMore && !loadingMore && apiTests.length > 0 && (
                <div className="flex justify-center py-4">
                  <Button 
                    onClick={() => loadApiTests(offset, 10, searchQuery, selectedProjectFilter)}
                    variant="outline"
                    className="w-full sm:w-auto"
                  >
                    {t("Load More Tests")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Chains Tab - Multi-Step API Workflows */}
        <TabsContent value="chains" className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t("API Test Chains")}</h3>
              <p className="text-sm text-gray-500">{t("Create multi-step workflows that chain API tests with variable propagation")}</p>
            </div>
            <Button
              onClick={() => {
                resetChainForm();
                setShowCreateChain(true);
              }}
              data-testid="btn-create-chain"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("Create Chain")}
            </Button>
          </div>

          {chains.length === 0 ? (
            <Card className="p-8 text-center">
              <Link2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t("No Chains Yet")}</h3>
              <p className="text-gray-500 mb-4 max-w-md mx-auto">
                {t("Create your first chain to link multiple API tests together. Variables extracted from one test can be automatically used in subsequent tests.")}
              </p>
              <div className="bg-gray-50 rounded-lg p-4 max-w-md mx-auto mb-4">
                <p className="text-sm text-gray-600 mb-2">{t("Example workflow:")}</p>
                <div className="flex items-center justify-center gap-2 text-sm">
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">POST /login</span>
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded">{"{{token}}"}</span>
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">GET /profile</span>
                </div>
              </div>
              <Button onClick={() => setShowCreateChain(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {t("Create Your First Chain")}
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {chains.map((chain) => (
                <ChainCard
                  key={chain.id}
                  chain={chain}
                  onExecute={executeChain}
                  onEdit={editChain}
                  onDelete={deleteChain}
                  isExecuting={chainExecuting === chain.id}
                  isAnyExecuting={chainExecuting !== null}
                />
              ))}
            </div>
          )}

          {/* Create/Edit Chain Dialog */}
          <Dialog open={showCreateChain} onOpenChange={(open) => {
            setShowCreateChain(open);
            if (!open) resetChainForm();
          }}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="chain-modal">
              <DialogHeader>
                <DialogTitle>{editingChain ? t("Edit Chain") : t("Create New Chain")}</DialogTitle>
                <DialogDescription>
                  {t("Build a multi-step API workflow. Variables extracted from each step will be available in subsequent steps.")}
                </DialogDescription>
              </DialogHeader>

              {/* Refactored Chain Builder Content */}
              <ChainBuilderContent
                chainForm={chainForm}
                onFormChange={setChainForm}
                environments={environments}
                apiTests={apiTests}
                onAddStep={addStepToChain}
                onRemoveStep={removeStepFromChain}
                onMoveStep={moveStepInChain}
                getMethodColor={getMethodColor}
              />

              {/* Footer */}
              <div className="border-t pt-4 flex justify-between items-center">
                <p className="text-sm text-gray-500">
                  {chainForm.steps.length === 0 ? t("Add at least one step") : t("{v0} step(s) configured", { v0: chainForm.steps.length })}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowCreateChain(false)} disabled={chainSubmitting}>
                    {t("Cancel")}
                  </Button>
                  <Button
                    onClick={createOrUpdateChain}
                    disabled={loading || chainSubmitting || !chainForm.name || chainForm.steps.length === 0}
                  >
                    {chainSubmitting ? t("Submitting...") : loading ? t("Saving...") : (editingChain ? t("Update Chain") : t("Create Chain"))}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Chain Execution Result Dialog - Refactored to separate component */}
          <Dialog open={showChainResult} onOpenChange={setShowChainResult}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t("Chain Execution Result")}</DialogTitle>
              </DialogHeader>
              <ChainExecutionResult result={chainExecutionResult} />
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Schedules Tab */}
        <TabsContent value="schedules" className="space-y-4">
          {loadingSchedules ? (
            <div className="text-center py-12">
              <RefreshCw className="h-8 w-8 text-gray-400 mx-auto mb-3 animate-spin" />
              <p className="text-gray-600">{t("Loading schedules...")}</p>
            </div>
          ) : schedules.length > 0 ? (
            /* Show schedules list immediately when schedules exist */
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <IDTooltip id="API-07" tooltip={t("Screen ID: {v0} - Use this ID when reporting issues or requesting features for this section", { v0: "API-07" })}>
                  <h3 className="text-lg font-semibold text-gray-900">{t("Test Schedules (")}{schedules.length})</h3>
                </IDTooltip>
                <Button
                  onClick={() => loadSchedules()}
                  variant="outline"
                  size="sm"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t("Refresh")}
                </Button>
              </div>
              <div className="grid gap-4">
                  {schedules.map((schedule) => (
                    <Card key={schedule.id} className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${
                            schedule.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                          }`} />
                          <div>
                            <h4 className="font-semibold text-gray-900">{schedule.test_name}</h4>
                            <div className="flex items-center space-x-2 mt-1">
                              <Badge variant="outline" className={
                                schedule.schedule_type === 'one_time' ? 'border-blue-500 text-blue-700' : 'border-purple-500 text-purple-700'
                              }>
                                {schedule.schedule_type === 'one_time' ? t("One-time") : t("{v0} recurring", {v0: schedule.recurrence_pattern})}
                              </Badge>
                              <Badge variant="outline">
                                {schedule.timezone ? (schedule.timezone.includes('/') ? schedule.timezone.split('/')[1].replace('_', ' ') : schedule.timezone) : 'UTC'}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              // Find the test and view its history
                              let test = apiTests.find(t => t.id === schedule.test_id);
                              
                              // If test not in current list, fetch it directly
                              if (!test) {
                                try {
                                  const response = await axios.get(
                                    `${BACKEND_URL}/api/api-testing/tests/${schedule.test_id}`
                                  );
                                  test = response.data;
                                } catch (error) {
                                  console.error('Could not load test:', error);
                                  return;
                                }
                              }
                              
                              if (test) {
                                // Set the selected test for history
                                setSelectedTestForHistory(test);
                                // Load executions (async but don't await - it will update state when done)
                                loadExecutions(test.id);
                                // Switch to history tab
                                setActiveTab('history');
                              }
                            }}
                            className="text-blue-600"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            {t("History")}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleSchedule(schedule.id)}
                            className={schedule.status === 'active' ? 'text-orange-600' : 'text-green-600'}
                          >
                            {schedule.status === 'active' ? t("Pause") : t("Activate")}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600"
                            onClick={() => openDeleteScheduleModal(schedule)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">{t("Next Run:")}</span>
                          <p>{schedule.next_run ? (() => {
                            // Convert UTC time back to user's timezone for display
                            const utcDate = new Date(schedule.next_run + (schedule.next_run.includes('Z') ? '' : 'Z'));
                            const options = { 
                              timeZone: schedule.timezone || 'America/New_York',
                              year: 'numeric',
                              month: '2-digit', 
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            };
                            return utcDate.toLocaleString(language === 'es' ? 'es-ES' : 'en-US', options);
                          })() : t("Not scheduled")}</p>
                        </div>
                        <div>
                          <span className="font-medium">{t("Executions:")}</span>
                          <p>{t('{v0} passed, {v1} failed', { v0: schedule.successful_executions, v1: schedule.failed_executions })}</p>
                        </div>
                        <div>
                          <span className="font-medium">{t("Last Execution:")}</span>
                          {schedule.latestExecution ? (
                            <div className="space-y-1">
                              <p className="flex items-center space-x-2">
                                <Badge variant="outline" className={
                                  schedule.latestExecution.result === 'PASS' ? 'border-green-500 text-green-700' : 'border-red-500 text-red-700'
                                }>
                                  {schedule.latestExecution.result}
                                </Badge>
                                {schedule.latestExecution.executionNumber && (
                                  <span className="text-xs text-gray-500">
                                    {t("Execution #")}{schedule.latestExecution.executionNumber}
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-gray-500">
                                {(() => {
                                  // Fix timezone conversion for execution time display
                                  const executedTimeString = schedule.latestExecution.completed_at || schedule.latestExecution.started_at;
                                  // Ensure UTC time is properly parsed
                                  const executedTime = new Date(executedTimeString + (executedTimeString.includes('Z') ? '' : 'Z'));
                                  const options = {
                                    timeZone: schedule.timezone || 'America/New_York',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  };
                                  return executedTime.toLocaleString(language === 'es' ? 'es-ES' : 'en-US', options);
                                })()}
                              </p>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400">{t("No executions yet")}</p>
                          )}
                        </div>
                      </div>

                      {/* Additional execution details */}
                      {schedule.latestExecution && schedule.total_executions > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <div className="flex items-center space-x-4">
                              <span>
                                <span className="font-medium">{t("Total Runs:")}</span> {schedule.total_executions}
                              </span>
                              <span>
                                <span className="font-medium">{t("Success Rate:")}</span> {
                                  schedule.total_executions > 0 
                                    ? Math.round((schedule.successful_executions / schedule.total_executions) * 100)
                                    : 0
                                }%
                              </span>
                              {schedule.last_run && (
                                <span>
                                  <span className="font-medium">{t("Last Run:")}</span> {
                                    (() => {
                                      // Fix timezone handling for last run time calculation
                                      const lastRunTime = new Date(schedule.last_run + (schedule.last_run.includes('Z') ? '' : 'Z'));
                                      const now = new Date();
                                      const diffMs = now - lastRunTime;
                                      const diffMins = Math.round(diffMs / (1000 * 60));
                                      const diffHours = Math.round(diffMs / (1000 * 60 * 60));
                                      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
                                      
                                      if (diffMins < 60) return t("{v0}m ago", {v0: diffMins});
                                      if (diffHours < 24) return t("{v0}h ago", {v0: diffHours});
                                      return t("{v0}d ago", {v0: diffDays});
                                    })()
                                  }
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {schedule.schedule_type === 'recurring' && schedule.end_date && (
                        <div className="mt-2 text-sm text-gray-600">
                          <span className="font-medium">{t("Ends:")}</span> {new Date(schedule.end_date).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US')}
                        </div>
                      )}
                    </Card>
                  ))}
              </div>
            </div>
          ) : (
            /* Show informational view when no schedules exist */
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <IDTooltip id="API-07" tooltip={t("Screen ID: {v0} - Use this ID when reporting issues or requesting features for this section", { v0: "API-07" })}>
                  <h3 className="text-lg font-semibold text-gray-900">{t("Test Schedules")}</h3>
                </IDTooltip>
              </div>
              <Card className="p-12 text-center">
                <Calendar className="h-16 w-16 text-purple-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t("Schedule Your API Tests")}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t("Set up automated test execution with flexible scheduling options")}
                </p>
                <div className="grid grid-cols-2 gap-4 text-left mb-6">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">{t("One-time scheduled runs")}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">{t("Daily, weekly, monthly recurring")}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">{t("Email & in-app notifications")}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">{t("Timezone support")}</span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-500">
                  {t("Click the \"Schedule\" button next to any test to get started")}
                </p>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Imports Tab */}
        <TabsContent value="imports" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <IDTooltip id="API-02" tooltip={t("Screen ID: {v0} - Use this ID when reporting issues or requesting features for this section", { v0: "API-02" })}>
                <h3 className="text-lg font-semibold text-gray-900">{t("API Collection Import")}</h3>
              </IDTooltip>
              <p className="text-gray-600">{t("Import Postman Collections or OpenAPI specifications")}</p>
            </div>
            <ImportCollectionModal
              open={showImportDialog}
              onOpenChange={setShowImportDialog}
              dragActive={dragActive}
              onDrop={handleFileDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              selectedFile={selectedFile}
              onFileSelect={(file) => setSelectedFile(file)}
              onClearFile={() => setSelectedFile(null)}
              onImport={uploadFile}
              importLoading={importLoading}
              trigger={
                <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                  <Upload className="h-4 w-4 mr-2" />
                  {t("Import Collection")}
                </Button>
              }
            />
          </div>

          {/* Import History */}
          {imports.length === 0 ? (
            <Card className="p-12 text-center">
              <Upload className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t("No imports yet")}
              </h3>
              <p className="text-gray-600 mb-6">
                {t("Import your first API collection to get started")}
              </p>
              <Button 
                onClick={() => setShowImportDialog(true)}
                className="bg-gradient-to-r from-blue-600 to-purple-600"
              >
                <Upload className="h-4 w-4 mr-2" />
                {t("Import Collection")}
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-gray-900">{t("Import History")}</h4>
              {imports.map((importJob) => (
                <Card key={importJob.id} className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      {getImportStatusIcon(importJob.status)}
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">{importJob.filename}</h4>
                        <div className="flex items-center space-x-2">
                          <Badge className={getImportStatusColor(importJob.status)}>
                            {t(importJob.status.toUpperCase())}
                          </Badge>
                          <Badge variant="outline">{importJob.format.replace('_', ' ')}</Badge>
                        </div>
                      </div>
                    </div>
                    <div className="text-right text-sm text-gray-600">
                      <p>{new Date(importJob.created_at).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US')}</p>
                      <p>{new Date(importJob.created_at).toLocaleTimeString(language === 'es' ? 'es-ES' : 'en-US')}</p>
                    </div>
                  </div>
                  
                  {importJob.status === 'completed' && (
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{importJob.created_tests}</p>
                        <p className="text-sm text-green-700">{t("Tests Created")}</p>
                      </div>
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <p className="text-2xl font-bold text-blue-600">{importJob.total_endpoints || 0}</p>
                        <p className="text-sm text-blue-700">{t("Total Endpoints")}</p>
                      </div>
                      <div className="text-center p-3 bg-purple-50 rounded-lg">
                        <p className="text-2xl font-bold text-purple-600">{importJob.skipped_endpoints || 0}</p>
                        <p className="text-sm text-purple-700">{t("Skipped")}</p>
                      </div>
                    </div>
                  )}
                  
                  {importJob.status === 'failed' && importJob.errors && importJob.errors.length > 0 && (
                    <Alert className="mt-4">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        {importJob.errors[0]}
                      </AlertDescription>
                    </Alert>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Environments Tab */}
        <TabsContent value="environments" className="space-y-4">
          <div className="flex justify-between items-center">
            <IDTooltip id="API-03" tooltip={t("Screen ID: {v0} - Use this ID when reporting issues or requesting features for this section", { v0: "API-03" })}>
              <h3 className="text-lg font-semibold">{t("Environments")}</h3>
            </IDTooltip>
            <Dialog open={showCreateEnvironment} onOpenChange={(open) => {
              setShowCreateEnvironment(open);
              if (open) {
                // Reset form when opening create dialog
                setEnvironmentForm({ name: '', description: '', variables: {} });
                setEnvironmentVariablesText('');
              }
            }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  {t("Add Environment")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("Create Environment")}</DialogTitle>
                  <DialogDescription>
                    {t("Set up a new environment with custom variables and base URL.")}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <Input
                    placeholder={t("Environment name (e.g., Development)")}
                    value={environmentForm.name}
                    onChange={(e) => setEnvironmentForm({...environmentForm, name: e.target.value})}
                  />
                  <Textarea
                    placeholder={t("Description (optional)")}
                    value={environmentForm.description}
                    onChange={(e) => setEnvironmentForm({...environmentForm, description: e.target.value})}
                  />
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      {t("Variables (JSON format)")}
                    </label>
                    <Textarea
                      placeholder='{"base_url": "https://api.dev.example.com", "api_version": "v1"}'
                      value={environmentVariablesText}
                      onChange={(e) => setEnvironmentVariablesText(e.target.value)}
                      rows={6}
                    />
                    <p className="text-xs text-gray-500 mt-1">{t("Enter valid JSON. Will be validated on save.")}</p>
                  </div>
                  <Button 
                    onClick={createEnvironment}
                    disabled={loading}
                    className="w-full"
                  >
                    {loading ? t("Creating...") : t("Create Environment")}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4">
            {environments.map((env) => (
              <EnvironmentCard
                key={env.id}
                environment={env}
                onEdit={editEnvironment}
                onDelete={openDeleteEnvironmentModal}
              />
            ))}
          </div>
        </TabsContent>

        {/* Credentials Tab */}
        <TabsContent value="credentials" className="space-y-4">
          <div className="flex justify-between items-center">
            <IDTooltip id="API-04" tooltip={t("Screen ID: {v0} - Use this ID when reporting issues or requesting features for this section", { v0: "API-04" })}>
              <h3 className="text-lg font-semibold">{t("API Credentials")}</h3>
            </IDTooltip>
            <Dialog open={showCreateCredential} onOpenChange={(open) => {
              setShowCreateCredential(open);
              // Reset form when dialog closes or opens fresh
              if (!open || !editingCredential) {
                setEditingCredential(null);
                setCredentialForm({ 
                  name: '', 
                  type: 'bearer_token', 
                  value: '', 
                  environment_id: '', 
                  oauth2_token_url: '', 
                  oauth2_client_id: '', 
                  oauth2_client_secret: '' 
                });
              }
            }}>
              <DialogTrigger asChild>
                <Button onClick={() => {
                  // Ensure fresh form when clicking Add Credential
                  setEditingCredential(null);
                  setCredentialForm({ 
                    name: '', 
                    type: 'bearer_token', 
                    value: '', 
                    environment_id: '', 
                    oauth2_token_url: '', 
                    oauth2_client_id: '', 
                    oauth2_client_secret: '' 
                  });
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t("Add Credential")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("Create API Credential")}</DialogTitle>
                  <DialogDescription>
                    {t("Add authentication credentials for your API endpoints.")}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <Input
                    placeholder={t("Credential name")}
                    value={credentialForm.name}
                    onChange={(e) => setCredentialForm({...credentialForm, name: e.target.value})}
                    autoComplete="off"
                    data-lpignore="true"
                    data-form-type="other"
                  />
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      {t("Type")}
                    </label>
                    <select
                      value={credentialForm.type}
                      onChange={(e) => setCredentialForm({...credentialForm, type: e.target.value, value: '', oauth2_token_url: '', oauth2_client_id: '', oauth2_client_secret: ''})}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    >
                      <option value="bearer_token">{t("Bearer Token")}</option>
                      <option value="api_key">{t("API Key")}</option>
                      <option value="jwt">{t("JWT Token")}</option>
                      <option value="oauth2">{t("OAuth2 Client Credentials")}</option>
                    </select>
                  </div>
                  
                  {credentialForm.type === 'oauth2' ? (
                    <div className="space-y-3">
                      <Input
                        type="text"
                        placeholder={t("Token URL (e.g., https://api.example.com/oauth/token)")}
                        value={credentialForm.oauth2_token_url || ''}
                        onChange={(e) => setCredentialForm({...credentialForm, oauth2_token_url: e.target.value})}
                        autoComplete="off"
                        data-lpignore="true"
                        data-form-type="other"
                      />
                      <Input
                        type="text"
                        placeholder={t("Client ID")}
                        value={credentialForm.oauth2_client_id || ''}
                        onChange={(e) => setCredentialForm({...credentialForm, oauth2_client_id: e.target.value})}
                        autoComplete="off"
                        data-lpignore="true"
                        data-form-type="other"
                      />
                      <Input
                        type="text"
                        placeholder={t("Client Secret")}
                        value={credentialForm.oauth2_client_secret || ''}
                        onChange={(e) => setCredentialForm({...credentialForm, oauth2_client_secret: e.target.value})}
                        autoComplete="new-password"
                        data-lpignore="true"
                        data-form-type="other"
                      />
                      <p className="text-xs text-gray-500">
                        {t("OAuth2 will automatically fetch a new access token before each API call.")}
                      </p>
                    </div>
                  ) : (
                    <Input
                      type="text"
                      placeholder={t("Enter credential value (API key, token, etc.)")}
                      value={credentialForm.value}
                      onChange={(e) => setCredentialForm({...credentialForm, value: e.target.value})}
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                    />
                  )}
                  
                  <Button 
                    onClick={createCredential}
                    disabled={loading}
                    className="w-full"
                  >
                    {loading ? t("Creating...") : t("Create Credential")}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4">
            {credentials.map((cred) => (
              <CredentialCard
                key={cred.id}
                credential={cred}
                onEdit={editCredential}
                onDelete={openDeleteCredentialModal}
              />
            ))}
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <IDTooltip id="API-06" tooltip={t("Screen ID: {v0} - Use this ID when reporting issues or requesting features for this section", { v0: "API-06" })}>
              <h3 className="text-lg font-semibold text-gray-900">{t("Test Execution History")}</h3>
            </IDTooltip>
            {selectedTestForHistory && (
              <div className="text-sm text-gray-600">
                {t("Test:")} <span className="font-medium">{selectedTestForHistory.name}</span>
              </div>
            )}
          </div>

          {selectedTestForHistory ? (
            <>
              {loadingExecutions ? (
                <div className="text-center py-8">
                  <RefreshCw className="h-8 w-8 text-gray-400 mx-auto mb-3 animate-spin" />
                  <p className="text-gray-600">{t("Loading execution history...")}</p>
                </div>
              ) : executions.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">{t("No executions found for this test")}</p>
                  <p className="text-sm text-gray-500 mt-2">{t("Run the test to see execution history")}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {executions.map((execution) => (
                    <ExecutionHistoryCard
                      key={execution.id}
                      execution={execution}
                      test={selectedTestForHistory}
                      onViewDetails={(exec) => {
                        setSelectedTest(exec);
                        setShowExecutionDetails(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <Clock className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">{t("Select a test to view its execution history")}</p>
              <p className="text-sm text-gray-500 mt-2">{t("Click the \"History\" button next to any test in the Tests tab")}</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Execution Details Dialog */}
      <ExecutionDetailsModal
        open={showExecutionDetails}
        onOpenChange={setShowExecutionDetails}
        execution={selectedTest}
        jiraIntegrations={jiraIntegrations}
        creatingJiraTicket={creatingJiraTicket}
        onCreateJiraTicket={createJiraTicketFromExecution}
      />

      {/* Import Results Dialog */}
      <ImportResultsModal
        open={showImportResults}
        onOpenChange={setShowImportResults}
        importResult={importResult}
        onViewTests={() => setActiveTab('tests')}
        onDone={() => setShowImportResults(false)}
      />

      {/* Edit Environment Dialog */}
      <Dialog open={showEditEnvironment} onOpenChange={setShowEditEnvironment}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("Edit Environment")}</DialogTitle>
            <DialogDescription>
              {t("Update environment variables and settings.")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder={t("Environment name (e.g., Development)")}
              value={environmentForm.name}
              onChange={(e) => setEnvironmentForm({...environmentForm, name: e.target.value})}
            />
            <Textarea
              placeholder={t("Description (optional)")}
              value={environmentForm.description}
              onChange={(e) => setEnvironmentForm({...environmentForm, description: e.target.value})}
            />
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                {t("Variables (JSON format)")}
              </label>
              <Textarea
                placeholder='{"base_url": "https://api.dev.example.com", "api_version": "v1"}'
                value={environmentVariablesText}
                onChange={(e) => setEnvironmentVariablesText(e.target.value)}
                rows={6}
              />
              <p className="text-xs text-gray-500 mt-1">{t("Enter valid JSON. Will be validated on save.")}</p>
            </div>
            <div className="flex space-x-2">
              <Button 
                onClick={updateEnvironment}
                disabled={loading}
                className="flex-1"
              >
                {loading ? t("Updating...") : t("Update Environment")}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditEnvironment(false);
                  setEditingEnvironment(null);
                  setEnvironmentForm({ name: '', description: '', variables: {} });
                }}
              >
                {t("Cancel")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Credential Dialog */}
      <Dialog open={showEditCredential} onOpenChange={setShowEditCredential}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("Edit Credential")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder={t("Credential name")}
              value={credentialForm.name}
              onChange={(e) => setCredentialForm({...credentialForm, name: e.target.value})}
            />
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                {t("Type")}
              </label>
              <select
                value={credentialForm.type}
                onChange={(e) => setCredentialForm({...credentialForm, type: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="bearer_token">{t("Bearer Token")}</option>
                <option value="api_key">{t("API Key")}</option>
                <option value="jwt">{t("JWT Token")}</option>
                <option value="oauth2">OAuth2</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                {t("New Credential Value")}
              </label>
              <Input
                type="password"
                placeholder={t("Enter new credential value (leave empty to keep current)")}
                value={credentialForm.value}
                onChange={(e) => setCredentialForm({...credentialForm, value: e.target.value})}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t("Leave empty to keep the current credential value")}
              </p>
            </div>
            <div className="flex space-x-2">
              <Button 
                onClick={createCredential}
                disabled={loading}
                className="flex-1"
              >
                {loading ? t("Updating...") : t("Update Credential")}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditCredential(false);
                  setEditingCredential(null);
                  setCredentialForm({ name: '', type: 'bearer_token', value: '', environment_id: '' });
                }}
              >
                {t("Cancel")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Test Modal */}
      <ScheduleModal
        open={showScheduleModal}
        onOpenChange={setShowScheduleModal}
        selectedTest={selectedTestForSchedule}
        scheduleForm={scheduleForm}
        setScheduleForm={setScheduleForm}
        onCreateSchedule={createSchedule}
        loading={loading}
      />

      {/* Delete API Test Confirmation Modal */}
      <Dialog open={showDeleteTestModal} onOpenChange={setShowDeleteTestModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              {t("Delete API Test")}
            </DialogTitle>
            <DialogDescription>
              {t("This action cannot be undone. This will permanently delete the test and all its execution history.")}
            </DialogDescription>
          </DialogHeader>
          
          {testToDelete && (
            <div className="py-4">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  {testToDelete.name}
                </p>
                <p className="text-xs text-gray-600 mb-2">
                  {testToDelete.method} {testToDelete.url}
                </p>
                {testToDelete.description && (
                  <p className="text-sm text-gray-600">
                    {testToDelete.description}
                  </p>
                )}
              </div>
              
              <p className="text-sm text-gray-700 mb-4">
                {t("Are you sure you want to delete this API test?")}
              </p>
              
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteTestModal(false);
                    setTestToDelete(null);
                  }}
                  disabled={loading}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  onClick={deleteApiTest}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      {t("Deleting...")}
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t("Delete Test")}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Schedule Confirmation Modal */}
      <Dialog open={showDeleteScheduleModal} onOpenChange={setShowDeleteScheduleModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              {t("Delete Schedule")}
            </DialogTitle>
            <DialogDescription>
              {t("This action cannot be undone. This will permanently delete the schedule.")}
            </DialogDescription>
          </DialogHeader>
          
          {scheduleToDelete && (
            <div className="py-4">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  {scheduleToDelete.schedule_type === 'one_time' ? t("One-time") : t("Recurring")} {t("Schedule")}
                </p>
                <p className="text-xs text-gray-600">
                  {scheduleToDelete.schedule_type === 'one_time' 
                    ? t("Scheduled for: {v0}", {v0: new Date(scheduleToDelete.scheduled_datetime).toLocaleString(language === 'es' ? 'es-ES' : 'en-US')})
                    : t("Recurring: {v0}", {v0: t(scheduleToDelete.recurrence_pattern)})
                  }
                </p>
              </div>
              
              <p className="text-sm text-gray-700 mb-4">
                {t("Are you sure you want to delete this schedule?")}
              </p>
              
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteScheduleModal(false);
                    setScheduleToDelete(null);
                  }}
                  disabled={loading}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  onClick={deleteSchedule}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      {t("Deleting...")}
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t("Delete Schedule")}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Environment Confirmation Modal */}
      <Dialog open={showDeleteEnvironmentModal} onOpenChange={setShowDeleteEnvironmentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              {t("Delete Environment")}
            </DialogTitle>
            <DialogDescription>
              {t("This action cannot be undone. This will permanently delete the environment and all its variables.")}
            </DialogDescription>
          </DialogHeader>
          
          {environmentToDelete && (
            <div className="py-4">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  {environmentToDelete.name}
                </p>
                {environmentToDelete.description && (
                  <p className="text-sm text-gray-600">
                    {environmentToDelete.description}
                  </p>
                )}
              </div>
              
              <p className="text-sm text-gray-700 mb-4">
                {t("Are you sure you want to delete this environment?")}
              </p>
              
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteEnvironmentModal(false);
                    setEnvironmentToDelete(null);
                  }}
                  disabled={loading}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  onClick={deleteEnvironment}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      {t("Deleting...")}
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t("Delete Environment")}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Credential Confirmation Modal */}
      <Dialog open={showDeleteCredentialModal} onOpenChange={setShowDeleteCredentialModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              {t("Delete Credential")}
            </DialogTitle>
            <DialogDescription>
              {t("This action cannot be undone. This will permanently delete the credential.")}
            </DialogDescription>
          </DialogHeader>
          
          {credentialToDelete && (
            <div className="py-4">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  {credentialToDelete.name}
                </p>
                <p className="text-xs text-gray-600">
                  {t("Type:")} {credentialToDelete.type.replace('_', ' ').toUpperCase()}
                </p>
              </div>
              
              <p className="text-sm text-gray-700 mb-4">
                {t("Are you sure you want to delete this credential?")}
              </p>
              
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteCredentialModal(false);
                    setCredentialToDelete(null);
                  }}
                  disabled={loading}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  onClick={deleteCredential}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      {t("Deleting...")}
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t("Delete Credential")}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Wrap the component with error boundary
const APITestingWithErrorBoundary = ({ user }) => {
  return (
    <APITestingErrorBoundary>
      <APITesting user={user} />
    </APITestingErrorBoundary>
  );
};

export default APITestingWithErrorBoundary;